import React, { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Add, AdminPanelSettings, BorderColor, PersonAdd, Search } from "@mui/icons-material";
import { Alert, IconButton } from "@mui/material";
import CustomTable from "../templates/CustomTable";
import { adminHeader } from "../utils/HeaderData";
import { collection, getDocs, doc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { initializeApp, deleteApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword, signOut as fbSignOut } from "firebase/auth";
import app from "../firebase";
import { useUser } from "../components/context/UserContext";
import { toast } from "react-toastify";

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.toLowerCase());

const ManageAdmin = () => {
  const navigate = useNavigate();
  const { userRole, loading } = useUser();

  const [admins, setAdmins] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("");
  const [createError, setCreateError] = useState("");
  const [createLoading, setCreateLoading] = useState(false);

  useEffect(() => {
    if (!loading && (userRole === "editor" || userRole === "operator")) navigate("/home");
  }, [loading, userRole, navigate]);

  const fetchAdmins = async () => {
    try {
      const snap = await getDocs(collection(db, "users"));
      const list = snap.docs.map((d) => ({
        _id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate() || new Date(0),
      }));
      list.sort((a, b) => b.createdAt - a.createdAt);
      setAdmins(list);
    } catch (err) {
      console.error(err.message);
    }
  };

  React.useEffect(() => {
    fetchAdmins();
  }, []);

  const openModal = () => {
    setNewName("");
    setNewEmail("");
    setNewRole("");
    setCreateError("");
    setModalOpen(true);
  };

  const handleCreate = async () => {
    if (!newName) return setCreateError("Name cannot be empty");
    if (!validateEmail(newEmail)) return setCreateError("Please enter a valid email");
    if (!newRole) return setCreateError("Please select a role");

    setCreateLoading(true);
    try {
      const secondaryApp = initializeApp(app.options, "secondary_" + Date.now());
      const secondaryAuth = getAuth(secondaryApp);
      const cred = await createUserWithEmailAndPassword(secondaryAuth, newEmail, "password123");
      const uid = cred.user.uid;
      await fbSignOut(secondaryAuth);
      await deleteApp(secondaryApp);

      await setDoc(doc(db, "users", uid), {
        name: newName,
        email: newEmail,
        role: newRole,
        createdAt: serverTimestamp(),
      });

      setModalOpen(false);
      toast("Admin created successfully!", { type: "success" });
      fetchAdmins();
    } catch (error) {
      if (error.code === "auth/email-already-in-use") {
        setCreateError("An account with this email already exists");
      } else {
        setCreateError("Error creating admin: " + error.message);
      }
    } finally {
      setCreateLoading(false);
    }
  };

  const [searchQuery, setSearchQuery] = useState("");

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [editRole, setEditRole] = useState("");
  const [editError, setEditError] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  const openEditModal = (row) => {
    setEditingAdmin(row);
    setEditRole(row.role || "");
    setEditError("");
    setEditModalOpen(true);
  };

  const handleUpdateRole = async () => {
    if (!editRole) return setEditError("Please select a role");

    setEditLoading(true);
    try {
      await updateDoc(doc(db, "users", editingAdmin._id), { role: editRole });
      setAdmins((prev) =>
        prev.map((a) => (a._id === editingAdmin._id ? { ...a, role: editRole } : a))
      );
      setEditModalOpen(false);
      toast("Role updated successfully!", { type: "success" });
    } catch (err) {
      setEditError("Error updating role: " + err.message);
    } finally {
      setEditLoading(false);
    }
  };

  const filteredAdmins = admins.filter((a) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (a.name || "").toLowerCase().includes(q) ||
      (a.role || "").toLowerCase().includes(q) ||
      (a.email || "").toLowerCase().includes(q)
    );
  });

  const canAddAdmin = userRole === "admin" || userRole === "superadmin";
  const roleOptions = userRole === "superadmin"
    ? [{ value: "operator", label: "Operator" }, { value: "editor", label: "Editor" }, { value: "admin", label: "Admin" }]
    : [{ value: "operator", label: "Operator" }, { value: "editor", label: "Editor" }];

  const inputClass = "w-full px-4 py-3 border border-gray-200 rounded-xl font-abel text-sm text-gray-700 focus:outline-none focus:border-[#000C66] focus:ring-2 focus:ring-[#000C66]/10 transition bg-white";
  const labelClass = "block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 font-abel";

  return (
    <Fragment>
      <div className="min-h-screen bg-[#f5f7fe] p-3 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#eef2ff] flex items-center justify-center flex-shrink-0">
                <AdminPanelSettings style={{ color: "#000C66", fontSize: 20 }} />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-[#000C66] font-aref">Manage Admins</h1>
                <p className="text-gray-400 text-xs font-abel">Manage administrator accounts</p>
              </div>
            </div>
            {canAddAdmin && (
              <button
                onClick={openModal}
                className="flex items-center justify-center gap-2 bg-[#000C66] text-white px-5 py-2.5 rounded-xl hover:bg-blue-900 transition font-abel text-sm font-semibold w-full sm:w-auto"
              >
                <Add style={{ fontSize: 18 }} /> Add Admin
              </button>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" style={{ fontSize: 18 }} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, role, email…"
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm font-abel text-gray-700 focus:outline-none focus:border-[#000C66] focus:ring-2 focus:ring-[#000C66]/10 transition bg-gray-50"
                />
              </div>
            </div>
            <CustomTable
              headers={adminHeader}
              data={filteredAdmins}
              editPath=""
              deletePath={userRole === "superadmin" ? "/delete-admin" : ""}
              extraActions={(row) =>
                canAddAdmin && row.role !== "superadmin" ? (
                  <IconButton
                    size="small"
                    onClick={() => openEditModal(row)}
                    sx={{ color: "#000C66", "&:hover": { backgroundColor: "#eef2ff" } }}
                  >
                    <BorderColor fontSize="small" />
                  </IconButton>
                ) : null
              }
            />
          </div>
        </div>
      </div>

      {/* Create Admin Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <PersonAdd style={{ color: "#000C66", fontSize: 20 }} />
                <h3 className="font-bold text-[#000C66] font-aref text-base">Add Admin</h3>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 transition text-xl font-bold leading-none"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4">
              {createError && (
                <Alert severity="error" onClose={() => setCreateError("")}>{createError}</Alert>
              )}

              <div>
                <label className={labelClass}>Full Name *</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => { setNewName(e.target.value); setCreateError(""); }}
                  placeholder="Admin's full name"
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>Email *</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => { setNewEmail(e.target.value); setCreateError(""); }}
                  placeholder="admin@example.com"
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>Role *</label>
                <select
                  value={newRole}
                  onChange={(e) => { setNewRole(e.target.value); setCreateError(""); }}
                  className={inputClass}
                >
                  <option value="">-- Select Role --</option>
                  {roleOptions.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>

              <div className="bg-amber-50 border border-amber-100 rounded-xl p-3">
                <p className="text-xs text-amber-700 font-abel">
                  Default password <strong>password123</strong> will be assigned. Ask the user to change it after first login.
                </p>
              </div>
            </div>

            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={() => setModalOpen(false)}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-abel font-medium hover:bg-gray-50 transition text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={createLoading}
                className="flex-1 py-3 rounded-xl bg-[#000C66] text-white font-abel font-semibold hover:bg-blue-900 transition text-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {createLoading ? (
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  "Create Admin"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {editModalOpen && editingAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <BorderColor style={{ color: "#000C66", fontSize: 20 }} />
                <h3 className="font-bold text-[#000C66] font-aref text-base">Edit Admin</h3>
              </div>
              <button
                onClick={() => setEditModalOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 transition text-xl font-bold leading-none"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4">
              {editError && (
                <Alert severity="error" onClose={() => setEditError("")}>{editError}</Alert>
              )}

              <div>
                <label className={labelClass}>Email</label>
                <input
                  type="email"
                  value={editingAdmin.email || ""}
                  disabled
                  className={`${inputClass} bg-gray-50 text-gray-400 cursor-not-allowed`}
                />
                <p className="text-[11px] text-gray-400 font-abel mt-1">
                  Editing login email isn't supported yet.
                </p>
              </div>

              <div>
                <label className={labelClass}>Role *</label>
                <select
                  value={editRole}
                  onChange={(e) => { setEditRole(e.target.value); setEditError(""); }}
                  className={inputClass}
                >
                  <option value="">-- Select Role --</option>
                  {roleOptions.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>

              <p className="text-[11px] text-gray-400 font-abel">
                Password changes aren't supported yet.
              </p>
            </div>

            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={() => setEditModalOpen(false)}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-abel font-medium hover:bg-gray-50 transition text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateRole}
                disabled={editLoading}
                className="flex-1 py-3 rounded-xl bg-[#000C66] text-white font-abel font-semibold hover:bg-blue-900 transition text-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {editLoading ? (
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  "Save Role"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default ManageAdmin;
